import {
  InfiniteData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "../api/api.ts";
import { ReviewContentFilters } from "@/lib/content-reviewer-filters.ts";

export type ContentToReview = {
  ai_rating: number | null;
  content_id: string;
  doc_id: string;
  gcs_url: string;
  image_caption: string;
  model: string;
  optimized_url: string;
  persona: string;
  persona_id: string;
  prompt: string;
  rating: number | null;
  status: string;
  thumbnail_url: string;
  timestamp: string;
  video_url: string | null;
  origin?: string;
};
type ContentPage = { data: ContentToReview[]; cursor?: string | null };
type Data = InfiniteData<ContentPage>;

async function fetchReviewContent(
  cursor: string | null,
  filters: Partial<ReviewContentFilters>,
  authenticated = true,
): Promise<ContentPage> {
  const url = authenticated
    ? `/api/content/paginated`
    : `/api/content/paginated/public`;
  const response = await apiClient.post(
    url,
    { ...filters, cursor },
    { withCredentials: true }, // Ensure cookies are sent with the request
  );

  const json = response.data;

  const data = json.data.sort(
    (a: any, b: any) =>
      new Date(b.timestamp.replace(/-/g, "/")).getTime() -
      new Date(a.timestamp.replace(/-/g, "/")).getTime(),
  );

  return { data, cursor: json.pagination?.cursor };
}

export function useReviewContentQuery(
  filters: Partial<ReviewContentFilters>,
  authenticated = true,
) {
  return useInfiniteQuery({
    queryKey: ["review-content", filters, authenticated],
    queryFn: ({ pageParam }) =>
      fetchReviewContent(pageParam, filters, authenticated),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.cursor,
  });
}

async function deleteReviewContent(docId: string) {
  const response = await apiClient.delete(`/api/content/${docId}`);

  if (response.status !== 200) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}

export function useDeleteReviewContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docId: string) => deleteReviewContent(docId),
    onSuccess: (_, docId) => {
      queryClient.setQueriesData(
        { queryKey: ["review-content"] },
        (prev: Data | undefined) => {
          if (!prev) return;

          return {
            ...prev,
            pages: prev.pages?.map((page) => ({
              ...page,
              data: page.data.filter((item) => item.doc_id !== docId),
            })),
          };
        },
      );
    },
  });
}

async function performActionOnReviewContent(docId: string, data: object) {
  const response = await apiClient.put(`/api/content/${docId}`, data);

  if (response.status !== 200) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}

export function usePerformActionOnReviewContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ docId, data }: { docId: string; data: object }) =>
      performActionOnReviewContent(docId, data),
    onSuccess: (_, { docId, data }) => {
      queryClient.setQueriesData(
        { queryKey: ["review-content"] },
        (prev: Data | undefined) => {
          if (!prev) return;

          return {
            ...prev,
            pages: prev.pages?.map((page) => ({
              ...page,
              data: page.data.map((item) =>
                item.doc_id === docId ? { ...item, ...data } : item,
              ),
            })),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ["review-content"] });
    },
  });
}

async function getAgentContentCount(agentId: string) {
  const response = await apiClient.get(
    `/api/content/persona/${agentId}/content-count`,
  );
  return response.data;
}

export function useGetAgentContentCountQuery(agentId: string) {
  return useQuery({
    queryKey: ["agent-content-count", agentId],
    queryFn: () => getAgentContentCount(agentId),
  });
}

async function fetchContent(docId: string) {
  const response = await apiClient.get<{ data: ContentToReview }>(
    `/api/content/${docId}`,
  );

  if (response.status !== 200) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.data.data;
}

export const contentQueries = {
  content: (docId: string) =>
    queryOptions({
      queryKey: ["review-content", docId],
      queryFn: () => fetchContent(docId),
    }),
};
